/**
 * UI helper that determines whether users browser is a Mac.
 */
const isMac = () => /macintosh|mac os x/i.test(navigator.userAgent)

export const KEY_SYMBOL = Object.freeze({
  shift: '⇧',
  control: 'Ctrl',
  option: 'Alt',
  command: '⌘',
  mod: isMac() ? '⌘' : 'Ctrl',
  delete: '⌫',
  enter: '⏎',
  escape: 'Esc',
  space: 'Space',
  up: '↑',
  down: '↓',
  left: '←',
  right: '→',
} as const)

export type KeySymbol = keyof typeof KEY_SYMBOL

export type ValidKey = KeySymbol | (string & {})

export type ShortcutKeysInput =
  | ValidKey
  | ValidKey[]
  | (ValidKey | ValidKey[])[]

type NormalizedShortcutGroup = {
  displayText: string
}

/**
 * Helper that handles nested arrays of shortcuts (+/or)
 *
 * @param keys `[ ['mod', 'b'], ['enter'] ]`
 */
export function normalizeShortcuts(
  keys: ShortcutKeysInput,
): NormalizedShortcutGroup[] {
  const rawGroups = Array.isArray(keys)
    ? keys.map((item) => (Array.isArray(item) ? item : [item]))
    : [[keys]]

  // Map string to `KEY_SYMBOL` values or fallback to uppercase
  return rawGroups.map((group) => {
    const transformedKeys = group.map((key) => {
      const lowerKey = key.toLowerCase()

      // Type-safe lookup
      if (lowerKey in KEY_SYMBOL) {
        return KEY_SYMBOL[lowerKey as KeySymbol]
      }

      // Fallback: Capitalize single letters
      return key.length === 1 ? key.toUpperCase() : key
    })

    return {
      displayText: transformedKeys.join('+'),
    }
  })
}
